/**
 * Offertes Lijst Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { offertesService, Offerte, OfferteStatus } from '../../services/offertes.service';
import './Offertes.css';
import './OffertesList.css';

const OffertesList = () => {
  const navigate = useNavigate();
  const [offertes, setOffertes] = useState<Offerte[]>([]);
  const [totalOffertes, setTotalOffertes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OfferteStatus | ''>('');
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWerkorderModal, setShowWerkorderModal] = useState(false);
  const [selectedOfferte, setSelectedOfferte] = useState<Offerte | null>(null);

  const loadOffertes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await offertesService.getAll({
        search: search || undefined,
        status: statusFilter || undefined,
        limit: 100
      });
      // API returns array directly, not wrapped in object
      if (Array.isArray(data)) {
        setOffertes(data);
        setTotalOffertes(data.length);
      } else {
        setOffertes(data.items || []);
        setTotalOffertes(data.total || 0);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de offertes');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadOffertes();
  }, [loadOffertes]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!offertes.length) return; // Wait for offertes to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [offertes]);

  const getStatusColor = (status: OfferteStatus): string => {
    switch (status) {
      case 'CONCEPT': return '#64748b'; // Grijs - in bewerking
      case 'VERSTUURD': return '#2563eb'; // Blauw - verzonden
      case 'GOEDGEKEURD': return '#059669'; // Groen - goedgekeurd
      case 'AFGEWEZEN': return '#dc2626'; // Rood - afgewezen
      case 'VERLOPEN': return '#f59e0b'; // Oranje - verlopen
      case 'INGETROKKEN': return '#64748b'; // Grijs - ingetrokken
      default: return '#64748b';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleStatusChange = async (offerteId: number, newStatus: OfferteStatus, e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    try {
      await offertesService.updateStatus(offerteId, newStatus);
      // Reload de lijst om de nieuwe status te tonen
      await loadOffertes();
    } catch (err: any) {
      alert(err.message || 'Fout bij het wijzigen van de status');
    }
  };

  const handleDelete = (offerte: Offerte, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOfferte(offerte);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedOfferte) return;
    try {
      await offertesService.delete(selectedOfferte.id);
      await loadOffertes();
      setShowDeleteModal(false);
      setSelectedOfferte(null);
    } catch (err: any) {
      alert(err.message || 'Fout bij het verwijderen van de offerte');
    }
  };

  const handleKopie = async (offerteId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await offertesService.kopie(offerteId);
      await loadOffertes();
    } catch (err: any) {
      alert(err.message || 'Fout bij het kopiëren van de offerte');
    }
  };

  const handleNaarWerkorder = (offerte: Offerte, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOfferte(offerte);
    setShowWerkorderModal(true);
  };

  const confirmWerkorder = async () => {
    if (!selectedOfferte) return;
    try {
      const result = await offertesService.naarWerkorder(selectedOfferte.id, {});
      setShowWerkorderModal(false);
      setSelectedOfferte(null);
      navigate(`/werkorders/${result.werkorder_id}`);
    } catch (err: any) {
      alert(err.message || 'Fout bij het aanmaken van de werkorder');
    }
  };

  if (loading) {
    return (
      <div className="offertes-container">
        <div className="loading">Offertes laden...</div>
      </div>
    );
  }

  return (
    <div className="offertes-container">
      <div className="page-header">
        <h1>Offertes</h1>
        <button className="btn btn-primary" onClick={() => navigate('/offertes/new')}>
          + Nieuwe Offerte
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Zoeken..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as OfferteStatus | '')}
          className="filter-select"
        >
          <option value="">Alle statussen</option>
          <option value="CONCEPT">Concept</option>
          <option value="VERSTUURD">Verstuurd</option>
          <option value="GOEDGEKEURD">Goedgekeurd</option>
          <option value="AFGEWEZEN">Afgewezen</option>
          <option value="VERLOPEN">Verlopen</option>
          <option value="INGETROKKEN">Ingetrokken</option>
        </select>
      </div>

      {/* Desktop Table */}
      <div className="offertes-table-container">
        {/* Vaste header rij (altijd zichtbaar) */}
        {offertes.length > 0 && (
          <div ref={tableHeaderRef} style={{
            display: 'flex',
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            padding: '0.75rem 1rem',
            marginTop: '1rem',
            borderRadius: '8px 8px 0 0',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#495057'
          }}>
            <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Klant</div>
            <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Datum</div>
            <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Onderwerp</div>
            <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Status</div>
            <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Geldig tot</div>
            <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
            <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
          </div>
        )}

        {/* Sticky header overlay (duplicaat) */}
        {isHeaderSticky && (
          <div style={{
            position: 'fixed',
            top: '185px',
            left: `${tableLeft}px`,
            width: `${tableWidth}px`,
            zIndex: 1000,
            display: 'flex',
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            borderRadius: '8px 8px 0 0',
            padding: '0.75rem 1rem',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#495057'
          }}>
            <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Klant</div>
            <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Datum</div>
            <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Onderwerp</div>
            <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Status</div>
            <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Geldig tot</div>
            <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
            <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
          </div>
        )}

        {/* Offertes lijst */}
        {offertes.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            {offertes.map((offerte) => (
              <div key={offerte.id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid #dee2e6',
                transition: 'background 0.2s',
                cursor: 'pointer'
              }}
              onClick={() => navigate(`/offertes/${offerte.id}`)}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{ flex: '1', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{offerte.relatie?.bedrijfsnaam === 'Particulier' ? `${offerte.relatie?.voornaam || ''} ${offerte.relatie?.tussenvoegsel || ''} ${offerte.relatie?.achternaam || ''}`.trim() : offerte.relatie?.bedrijfsnaam || '-'}</div>
                <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(offerte.offerte_datum)}</div>
                <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{offerte.onderwerp}</div>
                <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }} onClick={(e) => e.stopPropagation()}>
                  {offerte.status === 'GOEDGEKEURD' ? (
                    <span
                      className="btn btn-sm"
                      style={{
                        backgroundColor: getStatusColor(offerte.status),
                        color: 'white',
                        cursor: 'default',
                        width: '110px',
                        display: 'inline-block',
                        textAlign: 'center'
                      }}
                    >
                      Goedgekeurd
                    </span>
                  ) : (
                    <select
                      value={offerte.status}
                      onChange={(e) => handleStatusChange(offerte.id, e.target.value as OfferteStatus, e)}
                      disabled={!!offerte.werkorder_id}
                      className="btn btn-sm status-select"
                      style={{
                        backgroundColor: getStatusColor(offerte.status),
                        color: 'white',
                        cursor: offerte.werkorder_id ? 'not-allowed' : 'pointer',
                        opacity: offerte.werkorder_id ? 0.8 : 1,
                        width: '110px'
                      }}
                      title={offerte.werkorder_id ? 'Status kan niet gewijzigd worden: werkorder gekoppeld' : ''}
                    >
                      <option value="CONCEPT" style={{ backgroundColor: '#64748b', color: 'white' }}>Concept</option>
                      <option value="VERSTUURD" style={{ backgroundColor: '#2563eb', color: 'white' }}>Verstuurd</option>
                      <option value="AFGEWEZEN" style={{ backgroundColor: '#dc2626', color: 'white' }}>Afgewezen</option>
                      <option value="VERLOPEN" style={{ backgroundColor: '#f59e0b', color: 'white' }}>Verlopen</option>
                      <option value="INGETROKKEN" style={{ backgroundColor: '#64748b', color: 'white' }}>Ingetrokken</option>
                    </select>
                  )}
                </div>
                <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(offerte.geldig_tot)}</div>
                <div style={{ flex: '0.8', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatCurrency(offerte.totaal)}</div>
                <div className="actions" style={{ flex: '2', minWidth: 0, display: 'flex', gap: '0.25rem', flexWrap: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                  {offerte.status === 'GOEDGEKEURD' && !offerte.werkorder_id && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={(e) => handleNaarWerkorder(offerte, e)}
                      style={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center', flexShrink: 0 }}
                    >
                      Werkorder
                    </button>
                  )}
                  {offerte.werkorder_id && (
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => navigate(`/werkorders/${offerte.werkorder_id}`)}
                      style={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center', flexShrink: 0 }}
                    >
                      WO #{offerte.werkorder_id}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate(`/offertes/${offerte.id}`)}
                    style={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center', flexShrink: 0, color: 'white !important' }}
                  >
                    Openen
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => handleKopie(offerte.id, e)}
                    style={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center', flexShrink: 0 }}
                    title="Maak een kopie van deze offerte"
                  >
                    Kopiëren
                  </button>
                  {!offerte.werkorder_id && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={(e) => handleDelete(offerte, e)}
                      style={{ whiteSpace: 'nowrap', width: '80px', textAlign: 'center', flexShrink: 0 }}
                      title="Verwijder deze offerte"
                    >
                      Verwijderen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="mobile-offertes-cards">
        {offertes.map((offerte) => (
          <div key={offerte.id} className="mobile-offerte-card">
            <div className="mobile-offerte-header">
              {offerte.relatie?.bedrijfsnaam === 'Particulier' 
                ? `${offerte.relatie?.voornaam || ''} ${offerte.relatie?.tussenvoegsel || ''} ${offerte.relatie?.achternaam || ''}`.trim() 
                : offerte.relatie?.bedrijfsnaam || '-'}
            </div>
            <div className="mobile-offerte-content">
              <div className="mobile-offerte-row">
                <span className="mobile-offerte-label">Datum:</span>
                <span className="mobile-offerte-value">{formatDate(offerte.offerte_datum)}</span>
              </div>
              <div className="mobile-offerte-row">
                <span className="mobile-offerte-label">Onderwerp:</span>
                <span className="mobile-offerte-value">{offerte.onderwerp}</span>
              </div>
              <div className="mobile-offerte-row">
                <span className="mobile-offerte-label">Status:</span>
                <span className="mobile-offerte-value">
                  <span className={`mobile-offerte-status ${offerte.status.toLowerCase()}`}>
                    {offerte.status === 'CONCEPT' && 'Concept'}
                    {offerte.status === 'VERSTUURD' && 'Verstuurd'}
                    {offerte.status === 'GOEDGEKEURD' && 'Goedgekeurd'}
                    {offerte.status === 'AFGEWEZEN' && 'Afgewezen'}
                    {offerte.status === 'VERLOPEN' && 'Verlopen'}
                    {offerte.status === 'INGETROKKEN' && 'Ingetrokken'}
                  </span>
                </span>
              </div>
              <div className="mobile-offerte-row">
                <span className="mobile-offerte-label">Geldig tot:</span>
                <span className="mobile-offerte-value">{formatDate(offerte.geldig_tot)}</span>
              </div>
              <div className="mobile-offerte-row">
                <span className="mobile-offerte-label">Totaal:</span>
                <span className="mobile-offerte-value" style={{ fontWeight: 600 }}>{formatCurrency(offerte.totaal)}</span>
              </div>
              <div className="mobile-offerte-actions">
                <button
                  className="btn-small btn-primary"
                  onClick={() => navigate(`/offertes/${offerte.id}`)}
                  style={{ color: 'white !important' }}
                >
                  Openen
                </button>
                <button
                  className="btn-small btn-secondary"
                  onClick={(e) => handleKopie(offerte.id, e)}
                >
                  Kopiëren
                </button>
                {offerte.status === 'GOEDGEKEURD' && !offerte.werkorder_id && (
                  <button
                    className="btn-small btn-success"
                    onClick={(e) => handleNaarWerkorder(offerte, e)}
                  >
                    Werkorder
                  </button>
                )}
                {offerte.werkorder_id && (
                  <button
                    className="btn-small btn-info"
                    onClick={() => navigate(`/werkorders/${offerte.werkorder_id}`)}
                  >
                    WO #{offerte.werkorder_id}
                  </button>
                )}
                {!offerte.werkorder_id && (
                  <button
                    className="btn-small btn-danger"
                    onClick={(e) => handleDelete(offerte, e)}
                    style={{ color: 'white !important' }}
                  >
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {offertes.length === 0 && !loading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen offertes gevonden
        </div>
      )}

      {totalOffertes > 0 && (
        <div className="table-footer">
          <p>{offertes.length} van {totalOffertes} offerte(s) gevonden</p>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedOfferte && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc2626' }}>Offerte Verwijderen</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Weet je zeker dat je offerte <strong>{selectedOfferte.offertenummer}</strong> wilt verwijderen?
              <br /><br />
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Klant: {selectedOfferte.relatie?.bedrijfsnaam === 'Particulier'
                  ? `${selectedOfferte.relatie?.voornaam || ''} ${selectedOfferte.relatie?.tussenvoegsel || ''} ${selectedOfferte.relatie?.achternaam || ''}`.trim()
                  : selectedOfferte.relatie?.bedrijfsnaam || '-'}
              </span>
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowDeleteModal(false); setSelectedOfferte(null); }}
              >
                Annuleren
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Werkorder Modal */}
      {showWerkorderModal && selectedOfferte && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#059669' }}>Werkorder Aanmaken</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Wil je een werkorder aanmaken voor offerte <strong>{selectedOfferte.offertenummer}</strong>?
              <br /><br />
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Klant: {selectedOfferte.relatie?.bedrijfsnaam === 'Particulier'
                  ? `${selectedOfferte.relatie?.voornaam || ''} ${selectedOfferte.relatie?.tussenvoegsel || ''} ${selectedOfferte.relatie?.achternaam || ''}`.trim()
                  : selectedOfferte.relatie?.bedrijfsnaam || '-'}
                <br />
                Onderwerp: {selectedOfferte.onderwerp}
              </span>
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowWerkorderModal(false); setSelectedOfferte(null); }}
              >
                Annuleren
              </button>
              <button
                className="btn btn-success"
                onClick={confirmWerkorder}
              >
                Werkorder Aanmaken
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffertesList;
